// src/pages/MyTraining.tsx — programs + goals with cascading override display.
import { usePrograms, useGoals } from "@/lib/queries";
import { Card, PageHeader, Spinner, EmptyState, ErrorBanner } from "@/components/ui";
import type { Goal } from "@/lib/types";

// Show the resolved cascade value alongside whether it was overridden on the
// goal (set) or inherited from the athlete row (resolved* differs / own is null).
function CascadeField({ label, own, resolved }: { label: string; own: string | null; resolved: string | null | undefined }) {
  const value = resolved ?? own ?? null;
  if (!value) return null;
  const overridden = own != null;
  return (
    <span className="inline-flex items-center gap-1 rounded-md bg-white/5 px-2 py-0.5 text-xs">
      <span className="text-ls-muted">{label}:</span>
      <span className="text-ls-fg">{value}</span>
      {overridden ? (
        <span className="text-[10px] text-ls-accent" title="Overridden on this goal">
          ●
        </span>
      ) : (
        <span className="text-[10px] text-ls-muted" title="Inherited from athlete profile">
          ○
        </span>
      )}
    </span>
  );
}

function GoalRow({ goal }: { goal: Goal }) {
  return (
    <Card>
      <div className="flex items-start justify-between">
        <div>
          <p className="font-medium text-ls-fg">{goal.title}</p>
          {goal.metric ? (
            <p className="text-xs text-ls-muted">
              {goal.metric}
              {goal.targetValue != null ? `: ${goal.targetValue}${goal.unit ?? ""}` : ""}
            </p>
          ) : null}
        </div>
        <span className="rounded-full bg-white/5 px-2 py-0.5 text-[11px] text-ls-muted">
          {goal.status}
        </span>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <CascadeField label="sport" own={goal.sport} resolved={goal.resolvedSport} />
        <CascadeField label="position" own={goal.position} resolved={goal.resolvedPosition} />
        <CascadeField label="spec" own={goal.specialization} resolved={goal.resolvedSpecialization} />
        <CascadeField label="template" own={goal.goalTemplate} resolved={goal.resolvedGoalTemplate} />
      </div>
    </Card>
  );
}

export function MyTraining() {
  const programs = usePrograms();
  const goals = useGoals();

  return (
    <div className="space-y-6">
      <PageHeader title="My Training" subtitle="Programs and goals. ● overridden · ○ inherited." />

      <section>
        <h2 className="mb-3 text-sm font-medium uppercase tracking-wide text-ls-muted">Programs</h2>
        {programs.isLoading ? (
          <Spinner />
        ) : programs.error ? (
          <ErrorBanner message={(programs.error as Error).message} />
        ) : (programs.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No programs yet" />
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {programs.data!.items.map((p) => (
              <Card key={p.id}>
                <div className="flex items-center justify-between">
                  <p className="font-medium text-ls-fg">{p.title}</p>
                  <span className="rounded-full bg-white/5 px-2 py-0.5 text-[11px] text-ls-muted">
                    {p.phase}
                  </span>
                </div>
                {p.description ? (
                  <p className="mt-1 text-xs text-ls-muted">{p.description}</p>
                ) : null}
                {p.durationWeeks ? (
                  <p className="mt-2 text-xs text-ls-muted">{p.durationWeeks} weeks</p>
                ) : null}
              </Card>
            ))}
          </div>
        )}
      </section>

      <section>
        <h2 className="mb-3 text-sm font-medium uppercase tracking-wide text-ls-muted">Goals</h2>
        {goals.isLoading ? (
          <Spinner />
        ) : goals.error ? (
          <ErrorBanner message={(goals.error as Error).message} />
        ) : (goals.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No goals yet" />
        ) : (
          <div className="space-y-3">
            {goals.data!.items.map((g) => (
              <GoalRow key={g.id} goal={g} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
